import { useTheme } from '@mui/material/styles';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Chip,
    Typography
} from '@mui/material';

// project imports
import SubCard from 'ui-component/cards/SubCard';
import MainCard from 'ui-component/cards/MainCard';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { gridSpacing } from 'store/constant';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useScriptRef from 'hooks/useScriptRef';
import { openSnackbar } from 'store/slices/snackbar';
import axiosServices from 'utils/axios-services';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'store';
import { countries, Country, countryToFlag, getCountryFromCode } from 'utils/country';

const currencies = [
    {
        value: 'export',
        label: 'Export'
    },
    {
        value: 'technical',
        label: 'Technical'
    }
];

type Certificate = {
    usn: string;
    como: string[] | null;
    i_country: Country | null;
    ic: Country | null;
    e_country: Country | null;
    e_name: string;
    e_addr1: string;
    e_addr2: string;
    e_city: string;
    e_state: string;
    e_zip: string;
    i_name: string;
    i_addr1: string;
    i_addr2: string;
    i_city: string;
    i_state: string;
    i_zip: string;
    nop_10: number | null;
    carat_10: number | null;
    value_10: number | null;
    nop_21: number | null;
    carat_21: number | null;
    value_21: number | null;
    nop_31: number | null;
    carat_31: number | null;
    value_31: number | null;
    aes: string;
    an: string;
    doi: Date;
    doe: Date;
    status: string;
    add_note: string;
    nops?: number | null;
    type: string;
};
type FormValues = {
    usn: string;
    como: Country[] | null;

    e_country: Country | null;
    e_name: string;
    e_addr1: string;
    e_addr2: string;
    e_city: string;
    e_state: string;
    e_zip: string;

    ic: Country | null;
    i_country: Country | null;
    i_name: string;
    i_addr1: string;
    i_addr2: string;
    i_city: string;
    i_state: string;
    i_zip: string;

    nop_10: number | null;
    carat_10: number | null;
    value_10: number | null;
    nop_21: number | null;
    carat_21: number | null;
    value_21: number | null;
    nop_31: number | null;
    carat_31: number | null;
    value_31: number | null;

    add_note: string;
    aes: string;
    an: string;
    doi: Date | null;
    doe: Date | null;
    nops: number | null;
    status: string;
    type: string;
    submit: unknown | null;
};
const toNum = (v: unknown): number | null => (v === '' || v === null || v === undefined ? null : Number(v));
const toIntString = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? String(Math.trunc(n)) : '';
};
const toIntStringFromApi = (v: unknown) => {
    if (v === null || v === undefined || v === '') return '';
    // strip any decimals that may arrive (e.g., "12.00" -> "12")
    const s = String(v).replace(/\..*$/, '');
    // keep only digits (safety)
    const digits = s.replace(/\D/g, '');
    return digits;
};
const toIntOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : null;
};
const initialValues: FormValues = {
    usn: '',
    como: null,
    e_country: null,
    e_name: '',
    e_addr1: '',
    e_addr2: '',
    e_city: '',
    e_state: '',
    e_zip: '',
    ic: null,
    i_country: null,
    i_name: '',
    i_addr1: '',
    i_addr2: '',
    i_city: '',
    i_state: '',
    i_zip: '',
    nop_10: null,
    carat_10: null,
    value_10: null,
    nop_21: null,
    carat_21: null,
    value_21: null,
    nop_31: null,
    carat_31: null,
    value_31: null,
    add_note: '',
    aes: '',
    an: '',
    doi: null,
    doe: null,
    status: '',
    type: '',
    nops: null,
    submit: null
};

const validationSchema = yup.object({
    usn: yup.string().required('usn is required'),
    como: yup.array().of(yup.object()).min(1, 'At least one country of mining origin is required').nullable(),
    i_country: yup.object().required('i_country is required').nullable(),
    ic: yup.object().required('i_country is required').nullable(),
    e_country: yup.object().required('e_country is required').nullable(),
    e_name: yup.string().required('e_name is required'),
    e_addr1: yup.string().required('e_addr1 is required'),
    e_addr2: yup.string().required('usn is required'),
    e_city: yup.string().required('e_city is required'),
    e_state: yup.string().required('e_state is required'),
    e_zip: yup.string().required('e_zip is required'),
    i_name: yup.string().required('i_name is required'),
    i_addr1: yup.string().required('i_addr1 is required'),
    i_addr2: yup.string().required('i_addr2 is required'),
    i_city: yup.string().required('i_city is required'),
    i_state: yup.string().required('i_state is required'),
    i_zip: yup.string().required('i_zip is required'),
    nop_10: yup.number().required('nop_10 is required'),
    carat_10: yup
        .string()
        .required('carat_31 is required')
        .matches(/^[0-9]*\.?[0-9]{0,2}$/, 'carat_10 must be a valid decimal with up to two decimal places'),
    value_10: yup
        .string()
        .required('carat_31 is required')
        .matches(/^[0-9]*\.?[0-9]{0,2}$/, 'carat_10 must be a valid decimal with up to two decimal places'),
    nop_21: yup.number().required('nop_21 is required'),
    carat_21: yup
        .string()
        .required('carat_31 is required')
        .matches(/^[0-9]*\.?[0-9]{0,2}$/, 'value_10 must be a valid decimal with up to two decimal places'),
    value_21: yup
        .string()
        .required('carat_31 is required')
        .matches(/^[0-9]*\.?[0-9]{0,2}$/, 'value_21 must be a valid decimal with up to two decimal places'),
    nop_31: yup.number().required('nop_31 is required'),
    carat_31: yup
        .string()
        .required('carat_31 is required')
        .matches(/^[0-9]*\.?[0-9]{0,2}$/, 'carat_31 must be a valid decimal with up to two decimal places'),
    value_31: yup
        .string()
        .required('carat_31 is required')
        .matches(/^[0-9]*\.?[0-9]{0,2}$/, 'value_31 must be a valid decimal with up to two decimal places'),
    // aes: yup.string().required('aes is required'),
    an: yup.string().required('an is required'),
    doi: yup.date().required('doi is required').nullable(),
    doe: yup.date().required('doe is required').nullable()
});

export default function ViewCertificateDetails() {
    const theme = useTheme();
    const navigate = useNavigate();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    // const id = queryParams.get('id');
    const { id } = useParams();
    const scriptedRef = useScriptRef();
    const dispatch = useDispatch();
    const [countries_1, setCountries] = React.useState<readonly Country[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
    const [openNotFoundDialog, setOpenNotFoundDialog] = useState(false);

    // const handleClose = () => {
    //     setOpenPermissionDialog(false);
    // };

    // const handleIssue = async () => {};
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
                // setOpenIssueDialogIssueDialog(true);
                await axiosServices.put(`/certificates/${id}`, {
                    ...values,
                    como: values.como?.map((country) => country.code),
                    i_country: values.i_country?.code,
                    e_country: values.e_country?.code,
                    ic: values.ic?.code
                });

                if (scriptedRef.current) {
                    setStatus({ success: true });
                    setSubmitting(false);
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: 'Certificate updated successfully.',
                            variant: 'alert',
                            alert: {
                                color: 'success'
                            },
                            close: false
                        })
                    );

                    // setTimeout(() => {
                    //     navigate('/certificates', { replace: true });
                    // }, 100);
                }
            } catch (err: any) {
                console.error(err);
                if (scriptedRef.current) {
                    setStatus({ success: false });
                    setErrors({ submit: err.message });
                    setSubmitting(false);
                }
            }
        }
    });

    const getCertificate = async () => {
        try {
            const response = await axiosServices.get(`/certificates/${id}?id=${id}`);
            const certificateInfo: Certificate = response.data;

            // Step 1: Parse the JSON string (or use array if already parsed)
            const rawComo: string[] = Array.isArray(certificateInfo.como)
                ? (certificateInfo.como as string[])
                : JSON.parse(certificateInfo.como || '[]');

            // Step 2: Map each raw entry to a Country object, handling both "Label" and "Label-Code"
            const parsedComo: Country[] = rawComo
                .map((label: string) => {
                    // If the string contains a hyphen, assume format "CountryLabel-COUNTRYCODE"
                    if (label.includes('-')) {
                        const [countryLabel, code] = label.split('-');
                        return countries_1.find((c) => c.code.trim().toUpperCase() === code.trim().toUpperCase()) || null;
                    } else {
                        // Otherwise just match by label (no code part)
                        return countries_1.find((c) => c.code.trim().toLowerCase() === label.trim().toLowerCase()) || null;
                    }
                })
                .filter((c: Country | null): c is Country => !!c);
            formik.setValues({
                usn: certificateInfo?.usn || '',
                como: parsedComo,
                ic: getCountryFromCode((certificateInfo?.ic as unknown as string) || ''),
                i_country: getCountryFromCode((certificateInfo?.i_country as unknown as string) || ''),
                e_country: getCountryFromCode((certificateInfo?.e_country as unknown as string) || ''),
                e_name: certificateInfo?.e_name || '',
                e_addr1: certificateInfo?.e_addr1 || '',
                e_addr2: certificateInfo?.e_addr2 || '',
                e_city: certificateInfo?.e_city || '',
                e_state: certificateInfo?.e_state || '',
                e_zip: certificateInfo?.e_zip || '',
                i_name: certificateInfo?.i_name || '',
                i_addr1: certificateInfo?.i_addr1 || '',
                i_addr2: certificateInfo?.i_addr2 || '',
                i_city: certificateInfo?.i_city || '',
                i_state: certificateInfo?.i_state || '',
                i_zip: certificateInfo?.i_zip || '',
                nop_10: toIntOrNull(certificateInfo?.nop_10),
                carat_10: certificateInfo?.carat_10 ?? null,
                value_10: certificateInfo?.value_10 ?? null,
                nop_21: toIntOrNull(certificateInfo?.nop_21),
                carat_21: certificateInfo?.carat_21 ?? null,
                value_21: certificateInfo?.value_21 ?? null,
                nop_31: toIntOrNull(certificateInfo?.nop_31),
                carat_31: certificateInfo.carat_31 ?? null,
                value_31: certificateInfo?.value_31 ?? null,
                aes: certificateInfo?.aes || '',
                an: certificateInfo?.an || '',
                doi: certificateInfo?.doi || null,
                doe: certificateInfo?.doe || null,
                status: certificateInfo?.status || '',
                add_note: certificateInfo?.add_note || '',
                type: certificateInfo?.type || '',
                nops: toNum(certificateInfo?.nops),
                submit: null
            });
        } catch (err: any) {
            console.log(err.error);
            if (err.code === 402) {
                setOpenPermissionDialog(true);
            } else if (err.code === 404) {
                setOpenNotFoundDialog(true);
            }
        }
    };

    useEffect(() => {
        const getMiningOriginCountries = async () => {
            try {
                const response = await axiosServices.get('/mining-origin-countries');
                const countries1: Country[] = await response?.data?.map((country: any) => ({
                    label: country?.name,
                    code: country?.code,
                    phone: ''
                }));
                countries1.unshift({ label: 'Mix', code: 'MIX', phone: '' });

                setCountries(countries1);
                setLoading(true);

                if (scriptedRef.current && id !== '' && id !== undefined) {
                    await getCertificate();
                }
            } catch (err: any) {
                console.error(err.errors.usn[0]);
            }
        };
        getMiningOriginCountries();
    }, [id, scriptedRef]);
    useEffect(() => {
        if (countries_1.length > 0 && id) {
            getCertificate();
        }
    }, [countries_1, id]);
    const onClose = () => {
        navigate('/certificates', { replace: true });
    };

    return (
        <>
            <MainCard>
                <Grid sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h1">Full Certificate View</Typography>
                </Grid>
                <form onSubmit={formik.handleSubmit}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12}>
                                <SubCard title="Basic Infomation">
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Unique Serial ID"
                                                name="usn"
                                                value={formik.values.usn}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.usn && Boolean(formik.errors.usn)}
                                                // helperText={formik.touched.usn && formik.errors.usn}
                                                aria-readonly
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Status"
                                                value={formik.values.status}
                                                aria-readonly
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField
                                                id="outlined-select-currency"
                                                select
                                                label="Type"
                                                fullWidth
                                                name="type"
                                                value={formik.values.type}
                                                // onChange={formik.handleChange}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            >
                                                {currencies.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Autocomplete
                                                id="country-select-demo"
                                                value={formik.values.como || []}
                                                onChange={(_, value) => {
                                                    const mixOption = countries_1.find((c) => c.label === 'Mix');
                                                    const isMixSelected = value.some((v) => v.label === 'Mix');
                                                    if (isMixSelected || value.length >= 6) {
                                                        formik.setFieldValue('como', mixOption ? [mixOption] : []);
                                                    } else {
                                                        formik.setFieldValue('como', value);
                                                    }
                                                }}
                                                options={countries_1}
                                                isOptionEqualToValue={(option, value) =>
                                                    option.label === value.label && option.code === value.code
                                                }
                                                autoHighlight
                                                multiple
                                                disableClearable
                                                disabled
                                                getOptionLabel={(option) => option.label}
                                                renderTags={(value: Country[], getTagProps) =>
                                                    value.map((country: Country, idx: number) => (
                                                        <Chip
                                                            label={
                                                                <Typography sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                                                                    {country.label}
                                                                </Typography>
                                                            }
                                                            avatar={
                                                                <img
                                                                    loading="lazy"
                                                                    width="20"
                                                                    src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                                                                    srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                                                                    alt={country.label}
                                                                    style={{ borderRadius: '50%' }}
                                                                />
                                                            }
                                                            {...getTagProps({ index: idx })}
                                                            sx={{ m: 0.25 }}
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Country of Mining Origin"
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            readOnly: true
                                                        }}
                                                        sx={{
                                                            '& input': {
                                                                color: theme.palette.primary.main
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Autocomplete
                                                id="ic"
                                                options={countries}
                                                autoHighlight
                                                value={formik.values.ic}
                                                onChange={(_, value) => formik.setFieldValue('ic', value)}
                                                getOptionLabel={(option) => option.label}
                                                sx={{ mt: 4.5 }}
                                                readOnly
                                                renderOption={(props, option) => (
                                                    <Box component="li" sx={{ '& > img': { mr: 1, flexShrink: 0 } }} {...props}>
                                                        <li {...props} style={{ fontSize: 15 }}>
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                                style={{ marginRight: 10, fontSize: 18 }}
                                                            />
                                                            {option.label} ({option.code})
                                                        </li>
                                                    </Box>
                                                )}
                                                renderInput={(params) => {
                                                    const selectedCountry = formik.values.ic;

                                                    return (
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '.MuiAutocomplete-clearIndicator': { display: 'none' },
                                                                mt: selectedCountry?.code ? -2.3 : -1.2
                                                            }}
                                                        >
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                style={{
                                                                    marginLeft: '10px',
                                                                    marginRight: 10,
                                                                    zIndex: 9
                                                                }}
                                                                src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                            />
                                                            <TextField
                                                                name="ic"
                                                                error={formik.touched.ic && Boolean(formik.errors.ic)}
                                                                helperText={formik.touched.ic && formik.errors.ic}
                                                                {...params}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    '.MuiFilledInput-root': { border: 'none' },
                                                                    '& input': { marginLeft: '30px' }
                                                                }}
                                                                label="Issuing Country"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password' // disable autocomplete and autofill
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <SubCard title="Exporter">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Name"
                                                name="e_name"
                                                value={formik.values.e_name}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_name && Boolean(formik.errors.e_name)}
                                                // helperText={formik.touched.e_name && formik.errors.e_name}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line1"
                                                name="e_addr1"
                                                value={formik.values.e_addr1}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_addr1 && Boolean(formik.errors.e_addr1)}
                                                // helperText={formik.touched.e_addr1 && formik.errors.e_addr1}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line2"
                                                name="e_addr2"
                                                value={formik.values.e_addr2}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_addr2 && Boolean(formik.errors.e_addr2)}
                                                // helperText={formik.touched.e_addr2 && formik.errors.e_addr2}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Town/City"
                                                name="e_city"
                                                value={formik.values.e_city}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_city && Boolean(formik.errors.e_city)}
                                                // helperText={formik.touched.e_city && formik.errors.e_city}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="State"
                                                name="e_state"
                                                value={formik.values.e_state}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_state && Boolean(formik.errors.e_state)}
                                                // helperText={formik.touched.e_state && formik.errors.e_state}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="ZIP/Post Code"
                                                name="e_zip"
                                                value={formik.values.e_zip}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_zip && Boolean(formik.errors.e_zip)}
                                                // helperText={formik.touched.e_zip && formik.errors.e_zip}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                id="country-select-demo"
                                                options={countries}
                                                autoHighlight
                                                value={formik.values.e_country}
                                                onChange={(_, value) => formik.setFieldValue('e_country', value)}
                                                getOptionLabel={(option) => option.label}
                                                readOnly
                                                renderOption={(props, option) => (
                                                    <Box component="li" sx={{ '& > img': { mr: 1, flexShrink: 0 } }} {...props}>
                                                        <li {...props} style={{ fontSize: 15 }}>
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                                style={{ marginRight: 10, fontSize: 18 }}
                                                            />
                                                            {option.label} ({option.code})
                                                        </li>
                                                    </Box>
                                                )}
                                                renderInput={(params) => {
                                                    const selectedCountry = formik.values.e_country;
                                                    return (
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '.MuiAutocomplete-clearIndicator': { display: 'none' },
                                                                mt: selectedCountry?.code ? -2.3 : -1.2
                                                            }}
                                                        >
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                style={{
                                                                    marginLeft: '10px',
                                                                    marginRight: 10,
                                                                    zIndex: 9
                                                                }}
                                                                src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                            />
                                                            <TextField
                                                                name="e_country"
                                                                error={formik.touched.e_country && Boolean(formik.errors.e_country)}
                                                                helperText={formik.touched.e_country && formik.errors.e_country}
                                                                {...params}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    '.MuiFilledInput-root': { border: 'none' },
                                                                    '& input': { marginLeft: '30px' }
                                                                }}
                                                                label="Country"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password' // disable autocomplete and autofill
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                }}
                                                sx={{ mt: 4.5, mb: 3.4 }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <SubCard title="Importer">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Name"
                                                name="i_name"
                                                value={formik.values.i_name}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_name && Boolean(formik.errors.i_name)}
                                                // helperText={formik.touched.i_name && formik.errors.i_name}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line1"
                                                name="i_addr1"
                                                value={formik.values.i_addr1}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_addr1 && Boolean(formik.errors.i_addr1)}
                                                // helperText={formik.touched.i_addr1 && formik.errors.i_addr1}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line2"
                                                name="i_addr2"
                                                value={formik.values.i_addr2}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_addr2 && Boolean(formik.errors.i_addr2)}
                                                // helperText={formik.touched.i_addr2 && formik.errors.i_addr2}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Town/City"
                                                name="i_city"
                                                value={formik.values.i_city}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_city && Boolean(formik.errors.i_city)}
                                                // helperText={formik.touched.i_city && formik.errors.i_city}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="State"
                                                name="i_state"
                                                value={formik.values.i_state}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_state && Boolean(formik.errors.i_state)}
                                                // helperText={formik.touched.i_state && formik.errors.i_state}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="ZIP/Post Code"
                                                name="i_zip"
                                                value={formik.values.i_zip}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_zip && Boolean(formik.errors.i_zip)}
                                                // helperText={formik.touched.i_zip && formik.errors.i_zip}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                id="country-select-demo"
                                                options={countries}
                                                autoHighlight
                                                value={formik.values.i_country}
                                                onChange={(_, value) => formik.setFieldValue('i_country', value)}
                                                getOptionLabel={(option) => option.label}
                                                readOnly
                                                renderOption={(props, option) => (
                                                    <Box component="li" sx={{ '& > img': { mr: 1, flexShrink: 0 } }} {...props}>
                                                        <li {...props} style={{ fontSize: 15 }}>
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                                style={{ marginRight: 10, fontSize: 18 }}
                                                            />
                                                            {option.label} ({option.code})
                                                        </li>
                                                    </Box>
                                                )}
                                                renderInput={(params) => {
                                                    const selectedCountry = formik.values.i_country;
                                                    return (
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '.MuiAutocomplete-clearIndicator': { display: 'none' },
                                                                mt: selectedCountry?.code ? -2.3 : -1.2
                                                            }}
                                                        >
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                style={{
                                                                    marginLeft: '10px',
                                                                    marginRight: 10,
                                                                    zIndex: 9
                                                                }}
                                                                src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                            />
                                                            <TextField
                                                                name="i_country"
                                                                error={formik.touched.i_country && Boolean(formik.errors.i_country)}
                                                                helperText={formik.touched.i_country && formik.errors.i_country}
                                                                {...params}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    '.MuiFilledInput-root': { border: 'none' },
                                                                    '& input': { marginLeft: '30px' }
                                                                }}
                                                                label="Country"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password' // disable autocomplete and autofill
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                }}
                                                sx={{ mt: 4.5, mb: 3.4 }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>

                            <Grid item xs={12}>
                                <SubCard title="Certification Details">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sx={{ display: 'flex', backgroundColor: '#0c43b7' }}>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1" sx={{ color: '#fff', paddingBottom: '10px' }}>
                                                    HS CODE
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1" sx={{ color: '#fff', paddingBottom: '10px' }}>
                                                    No. Parcels
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1" sx={{ color: '#fff', paddingBottom: '10px' }}>
                                                    Total Caratage
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1" sx={{ color: '#fff', paddingBottom: '10px' }}>
                                                    USD Value
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Divider
                                            flexItem
                                            sx={{ width: '100%', ml: 1, mt: 1, border: `1px solid ${theme.palette.primary}` }}
                                        />
                                        <Grid item xs={12} sx={{ display: 'flex' }} container spacing={2}>
                                            <Grid item xs={3} sx={{ ml: -1, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1" sx={{ ml: 1 }}>
                                                    7102.10 Unsorted Rough
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="nop_10"
                                                    value={formik.values.nop_10 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.nop_10 && Boolean(formik.errors.nop_10)}
                                                    // helperText={formik.touched.nop_10 && formik.errors.nop_10}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="carat_10"
                                                    value={formik.values.carat_10 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.carat_10 && Boolean(formik.errors.carat_10)}
                                                    // helperText={formik.touched.carat_10 && formik.errors.carat_10}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="value_10"
                                                    value={formik.values.value_10 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.value_10 && Boolean(formik.errors.value_10)}
                                                    // helperText={formik.touched.value_10 && formik.errors.value_10}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sx={{ display: 'flex' }} container spacing={2}>
                                            <Grid item xs={3} sx={{ ml: -1, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1" sx={{ ml: 1 }}>
                                                    7102.21 Industrial Diamonds
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="nop_21"
                                                    value={formik.values.nop_21 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.nop_21 && Boolean(formik.errors.nop_21)}
                                                    // helperText={formik.touched.nop_21 && formik.errors.nop_21}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="carat_21"
                                                    value={formik.values.carat_21 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.carat_21 && Boolean(formik.errors.carat_21)}
                                                    // helperText={formik.touched.carat_21 && formik.errors.carat_21}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="value_21"
                                                    value={formik.values.value_21 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.value_21 && Boolean(formik.errors.value_21)}
                                                    // helperText={formik.touched.value_21 && formik.errors.value_21}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sx={{ display: 'flex' }} container spacing={2}>
                                            <Grid item xs={3} sx={{ ml: -1, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1" sx={{ ml: 1 }}>
                                                    7102.31 Rough Diamonds, Gems
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="nop_31"
                                                    value={formik.values.nop_31 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.nop_31 && Boolean(formik.errors.nop_31)}
                                                    // helperText={formik.touched.nop_31 && formik.errors.nop_31}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="carat_31"
                                                    value={formik.values.carat_31 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.carat_31 && Boolean(formik.errors.carat_31)}
                                                    // helperText={formik.touched.carat_31 && formik.errors.carat_31}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="value_31"
                                                    value={formik.values.value_31 ?? ''}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.value_31 && Boolean(formik.errors.value_31)}
                                                    // helperText={formik.touched.value_31 && formik.errors.value_31}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12}>
                                <SubCard title="Number of Parcels in Shipment">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="nops"
                                                name="nops"
                                                label="Number of Parcels"
                                                value={formik.values.nops ?? ''}
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12}>
                                <SubCard title="Add Note">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="add_note"
                                                label="Notes"
                                                name="add_note"
                                                value={formik.values.add_note}
                                                onChange={formik.handleChange}
                                                multiline
                                                disabled
                                                minRows={3} // You can adjust this to control the height
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12}>
                                <SubCard title="Others Infomation">
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Authorized eSignature"
                                                value={formik.values.aes}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <DatePicker
                                                label="Date of Issuance"
                                                value={formik.values.doi}
                                                onChange={(newValue) => formik.setFieldValue('doi', newValue)}
                                                readOnly
                                                renderInput={(params) => <TextField {...params} fullWidth name="doi" />}
                                                inputFormat="MM-dd-yyyy"
                                                mask="__-__-____"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Authorized Name"
                                                name="an"
                                                value={formik.values.an}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.an && Boolean(formik.errors.an)}
                                                // helperText={formik.touched.an && formik.errors.an}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <DatePicker
                                                label="Date of Expiry"
                                                value={formik.values.doe}
                                                onChange={(_newValue) => {
                                                    formik.setFieldValue('doe', formik.values.doe);
                                                }}
                                                inputFormat="MM-dd-yyyy"
                                                // mask="__-__-____"
                                                readOnly
                                                renderInput={(params) => <TextField {...params} fullWidth name="doe" />}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            {/* <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                    <AnimateButton>
                                        <Button variant="outlined" size="large" onClick={onClose}>
                                            Cancel
                                        </Button>
                                    </AnimateButton>
                                    <AnimateButton>
                                        <Button variant="contained" type="submit" size="large">
                                            Save Certificate
                                        </Button>
                                    </AnimateButton>
                                </Stack>
                            </Grid> */}
                        </Grid>
                    </LocalizationProvider>
                </form>
                <Dialog
                    open={openPermissionDialog}
                    onClose={onClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    sx={{ p: 3 }}
                    disableEscapeKeyDown
                >
                    <DialogTitle id="alert-dialog-title" sx={{ mt: 2, mb: 2 }}>
                        You do not have access to view this certificate.
                    </DialogTitle>
                    {/* <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            <Typography variant="body2" component="span">
                                Issuing a certification does not send or notify the exporter or importer.
                            </Typography>
                        </DialogContentText>
                    </DialogContent> */}
                    <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            sx={{ minWidth: '150px' }}
                            onClick={() => {
                                onClose();
                            }}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={openNotFoundDialog}
                    onClose={onClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    sx={{ p: 3 }}
                    disableEscapeKeyDown
                >
                    <DialogTitle id="alert-dialog-title" sx={{ mt: 2, mb: 2 }}>
                        Can not find your certificate.
                        <br />
                        {`Please check again your certificate UUID - ${id}.`}
                    </DialogTitle>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            sx={{ minWidth: '150px' }}
                            onClick={() => {
                                onClose();
                            }}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </MainCard>
        </>
    );
}
